import { TargetIcon } from '@radix-ui/react-icons';
import { Box, Flex } from '@radix-ui/themes';
import { clamp } from 'lodash';
import { PointerEvent as ReactPointerEvent, useRef, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';

const STORAGE_KEY = 'inserterCameraCrosshair.positions';

type LineKey = 'y1' | 'y2' | 'x';
type CrosshairPositions = Record<LineKey, number>;

const DEFAULTS: CrosshairPositions = { y1: 50, y2: 47, x: 50 };

const loadCrosshairPositions = (): CrosshairPositions => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return DEFAULTS;
    }

    return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {
    return DEFAULTS;
  }
};

export const Crosshair = () => {
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);
  const [showCrosshair, setShowCrosshair] = useState<boolean>(false);

  const [crosshairPositions, setCrosshairPositions] =
    useState<CrosshairPositions>(loadCrosshairPositions);

  const overlayRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<LineKey | null>(null);

  if (!userRoleFeatureFlags.controls.inserterCameraCrosshair) {
    return null;
  }

  // pointer capture lets the drag continue even if the cursor leaves the line
  const handlePointerDown =
    (key: LineKey) => (e: ReactPointerEvent<HTMLDivElement>) => {
      draggingRef.current = key;
      e.currentTarget.setPointerCapture(e.pointerId);
    };

  // convert the pointer position to a percentage of the overlay, clamped to the image bounds
  const handlePointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const key = draggingRef.current;
    const rect = overlayRef.current?.getBoundingClientRect();

    if (!key) {
      return;
    }

    if (!rect) {
      return;
    }

    if (rect.width === 0 || rect.height === 0) {
      return;
    }

    // convert the pointer position to a percentage of the overlay, clamped to the image bounds
    const pct =
      key === 'x'
        ? clamp(((e.clientY - rect.top) / rect.height) * 100, 0, 100)
        : clamp(((e.clientX - rect.left) / rect.width) * 100, 0, 100);

    setCrosshairPositions((prev) => ({ ...prev, [key]: pct }));
  };

  // save once at the end of a drag
  const handlePointerEnd = () => {
    if (!draggingRef.current) {
      return;
    }

    draggingRef.current = null;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(crosshairPositions));
  };

  return (
    <>
      <Box
        position="absolute"
        bottom="8px"
        left="8px"
        onClick={() => setShowCrosshair((prev) => !prev)}
        style={{ cursor: 'pointer' }}
      >
        <TargetIcon />
      </Box>

      {showCrosshair && (
        <Flex
          ref={overlayRef}
          className="crosshair-overlay"
          height="100%"
          width="100%"
          top="0"
          left="0"
          position="absolute"
        >
          <Box
            className="crosshair-line crosshair-line-y"
            style={{
              left: `${crosshairPositions.y1}%`,
              transform: 'translateX(-50%)',
            }}
            onPointerDown={handlePointerDown('y1')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onLostPointerCapture={handlePointerEnd}
          />
          <Box
            className="crosshair-line crosshair-line-y"
            style={{
              left: `${crosshairPositions.y2}%`,
              transform: 'translateX(-50%)',
            }}
            onPointerDown={handlePointerDown('y2')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onLostPointerCapture={handlePointerEnd}
          />
          <Box
            className="crosshair-line crosshair-line-x"
            style={{
              top: `${crosshairPositions.x}%`,
              transform: 'translateY(-50%)',
            }}
            onPointerDown={handlePointerDown('x')}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerEnd}
            onLostPointerCapture={handlePointerEnd}
          />
        </Flex>
      )}
    </>
  );
};
